import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { ProductBento } from "@/components/ProductBento";
import { Gallery } from "@/components/Gallery";
import { CraftStory } from "@/components/CraftStory";
import { Markets } from "@/components/Markets";
import { EnquiryForm } from "@/components/EnquiryForm";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Footer } from "@/components/Footer";
import About from "@/pages/About";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import CollectionPage from "@/pages/CollectionPage";
import { Seo } from "@/components/Seo";

const HOME_TITLE = "Leather Goods Manufacturer & Exporter | FnB International";
const HOME_DESC = "Manufacturer & exporter of leather bags, wallets, handmade candles and fridge magnets from Kanpur, India. Made-to-order, OEM and private label.";

const Home = ({ onNavigate, onEnquire, preselected }) => {
    const location = useLocation();
    useEffect(() => {
        if (location.state?.category) {
            onEnquire(location.state.category);
            window.history.replaceState({}, "");
        }
    }, [location.state, onEnquire]);
    return (
    <main>
        <Seo title={HOME_TITLE} description={HOME_DESC} path="/" />
        <Hero onNavigate={onNavigate} />
        <Marquee />
        <ProductBento onEnquire={onEnquire} />
        <Gallery />
        <CraftStory />
        <Markets onNavigate={onNavigate} />
        <EnquiryForm preselected={preselected} />
    </main>
    );
};

const ScrollManager = ({ scrollTo }) => {
    const location = useLocation();
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.slice(1);
            const t = setTimeout(() => scrollTo(id), 500);
            return () => clearTimeout(t);
        }
        window.scrollTo(0, 0);
    }, [location, scrollTo]);
    return null;
};

const Shell = ({ onNavigate, onEnquire, preselected, scrollTo }) => {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return (
            <div className="bg-[#0F0C08] min-h-screen">
                <AuthCallback />
                <Toaster theme="dark" position="bottom-center" />
            </div>
        );
    }
    const bare = location.pathname === "/login" || location.pathname === "/admin";
    return (
        <div className="bg-[#0F0C08] min-h-screen">
            <ScrollManager scrollTo={scrollTo} />
            {!bare && <Navbar onNavigate={onNavigate} />}
            <Routes>
                <Route
                    path="/"
                    element={<Home onNavigate={onNavigate} onEnquire={onEnquire} preselected={preselected} />}
                />
                <Route path="/about" element={<About />} />
                <Route path="/collections/:slug" element={<CollectionPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin" element={<Admin />} />
            </Routes>
            {!bare && <Footer onNavigate={onNavigate} />}
            {!bare && <WhatsAppFloat />}
            <Toaster theme="dark" position="bottom-center" />
        </div>
    );
};

function App() {
    const lenisRef = useRef(null);
    const [preselected, setPreselected] = useState(null);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        lenisRef.current = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        if (lenisRef.current) {
            lenisRef.current.scrollTo(el, { offset: -70 });
        } else {
            el.scrollIntoView({ behavior: "smooth" });
        }
    };

    const enquireAbout = (category) => {
        setPreselected(category);
        scrollTo("enquiry");
    };

    return (
        <BrowserRouter>
            <Shell onNavigate={scrollTo} onEnquire={enquireAbout} preselected={preselected} scrollTo={scrollTo} />
        </BrowserRouter>
    );
}

export default App;
